import { useEffect, useMemo, useState } from 'react';
import { Autocomplete, Box, Modal, Stack, TextField } from '@mui/material';
import CustomText from 'components/common/CustomText/CustomText';
import CustomButton from 'components/buttons/CustomButton/CustomButton';
import { colors } from 'styles/colors';
import { LeafCategoryOption } from 'services/rawProductService';

const SUGGESTED_GROUP = 'Suggested by the graph';
const ALL_GROUP = 'All categories';

type PickerOption = LeafCategoryOption & { group: string };

interface Props {
  open: boolean;
  productName: string;
  // Where the product is filed now - the graph's pick, or an earlier correction
  // of it. Null until the category pass has run.
  current: { id: string; name: string } | null;
  // The leaves the graph searched, first in the list. The right one is often not
  // among them - which is how a gap in the taxonomy shows itself - so the whole
  // taxonomy sits behind them.
  candidates: LeafCategoryOption[];
  leafCategories: LeafCategoryOption[];
  isSaving: boolean;
  onClose: () => void;
  onSave: (leafCategoryId: string) => void;
}

export default function CategoryPickerModal({
  open,
  productName,
  current,
  candidates,
  leafCategories,
  isSaving,
  onClose,
  onSave,
}: Props) {
  const [selected, setSelected] = useState<PickerOption | null>(null);

  const options = useMemo<PickerOption[]>(() => {
    // Candidate paths were snapshotted when the graph ran, so a category that
    // has since been renamed or moved still carries its old path. Resolve each
    // by id against the live list and drop any whose category no longer exists -
    // it could not be saved anyway.
    const liveById = new Map(leafCategories.map((category) => [category.id, category]));
    const resolved = candidates
      .map((candidate) => liveById.get(candidate.id))
      .filter((candidate): candidate is LeafCategoryOption => Boolean(candidate));
    const candidateIds = new Set(resolved.map((candidate) => candidate.id));

    return [
      ...resolved.map((candidate) => ({ ...candidate, group: SUGGESTED_GROUP })),
      ...leafCategories
        .filter((category) => !candidateIds.has(category.id))
        .map((category) => ({ ...category, group: ALL_GROUP })),
    ];
  }, [candidates, leafCategories]);

  // Opens on what the row holds, so a reviewer sees what they are changing
  // rather than an empty box.
  const openOn = current?.id ?? null;
  useEffect(() => {
    if (!open) return;
    setSelected(options.find((option) => option.id === openOn) ?? null);
  }, [open, openOn, options]);

  return (
    <Modal open={open} onClose={isSaving ? undefined : onClose}>
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          bgcolor: 'background.paper',
          borderRadius: 2,
          boxShadow: 24,
          p: 3,
          width: 560,
          maxWidth: '92vw',
        }}
      >
        <CustomText text="Change the category" fontWeight={700} fontSize={18} />
        <Box mt={0.5}>
          <CustomText text={productName} fontSize={13} color={colors.gray} />
        </Box>

        <Box mt={2}>
          <Autocomplete
            options={options}
            value={selected}
            disabled={isSaving}
            onChange={(_event, value) => setSelected(value)}
            groupBy={(option) => option.group}
            getOptionLabel={(option) => option.pathEN}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            renderInput={(params) => <TextField {...params} size="small" label="Category" autoFocus />}
          />
        </Box>

        {/* The candidates the graph searched come first in the list. The right
            leaf being absent from them is worth noticing rather than working
            around: it is how a gap in the taxonomy shows itself. */}
        <Box mt={1}>
          <CustomText
            text="The leaves the graph searched are listed first, the rest of the taxonomy behind them."
            fontSize={11}
            color={colors.lightgray}
          />
        </Box>

        <Stack direction="row" justifyContent="flex-end" spacing={2} mt={3}>
          <CustomButton
            text="Cancel"
            variant="outlined"
            height={40}
            disabled={isSaving}
            onClick={onClose}
          />
          <CustomButton
            text="Save"
            height={40}
            isLoading={isSaving}
            disabled={!selected || selected.id === current?.id}
            onClick={() => {
              if (selected) onSave(selected.id);
            }}
          />
        </Stack>
      </Box>
    </Modal>
  );
}
